import { useState, useEffect, useCallback } from 'react'
import { FilterValues, SavedFilter } from '@/shared/components/entities/AdvancedFilters'

interface UseAdvancedFiltersOptions {
  entityType: string // Ex: 'categories', 'assets', 'units'
  userId?: string
  onFiltersChange?: (filters: FilterValues) => void
}

export function useAdvancedFilters({
  entityType,
  userId,
  onFiltersChange
}: UseAdvancedFiltersOptions) {
  const [filterValues, setFilterValues] = useState<FilterValues>({})
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([])
  const [loading, setLoading] = useState(true)
  const [defaultFilterApplied, setDefaultFilterApplied] = useState(false)

  // Load saved filters from localStorage (ou Supabase)
  useEffect(() => {
    loadSavedFilters()
  }, [entityType, userId])

  // Load default filter on mount (only once)
  useEffect(() => {
    if (!defaultFilterApplied && savedFilters.length > 0) {
      const defaultFilter = savedFilters.find(f => f.is_default)
      if (defaultFilter && Object.keys(filterValues).length === 0) {
        setFilterValues(defaultFilter.filters)
        setDefaultFilterApplied(true)
      }
    }
  }, [savedFilters, defaultFilterApplied])

  const loadSavedFilters = async () => {
    try {
      setLoading(true)
      
      // Por enquanto usando localStorage
      // Depois pode ser implementado com Supabase
      const storageKey = `filters_${entityType}_${userId || 'global'}`
      const saved = localStorage.getItem(storageKey)
      
      if (saved) {
        setSavedFilters(JSON.parse(saved))
      }
    } catch (error) {
      console.error('Error loading saved filters:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFiltersChange = useCallback((values: FilterValues) => {
    setFilterValues(values)
    setDefaultFilterApplied(true) // Mark as applied to prevent auto-reapplication
    onFiltersChange?.(values)
  }, [onFiltersChange])

  const saveFilter = async (name: string, isDefault: boolean) => {
    try {
      const newFilter: SavedFilter = {
        id: `filter_${Date.now()}`,
        name,
        filters: filterValues,
        created_at: new Date().toISOString(),
        is_default: isDefault
      }

      let updatedFilters = [...savedFilters, newFilter]

      // Se for padrão, remove o padrão anterior
      if (isDefault) {
        updatedFilters = updatedFilters.map(f => ({
          ...f,
          is_default: f.id === newFilter.id
        }))
      }

      setSavedFilters(updatedFilters)

      // Salvar no localStorage
      const storageKey = `filters_${entityType}_${userId || 'global'}`
      localStorage.setItem(storageKey, JSON.stringify(updatedFilters))

      return newFilter.id
    } catch (error) {
      console.error('Error saving filter:', error)
      throw error
    }
  }

  const loadFilter = async (filterId: string) => {
    try {
      const filter = savedFilters.find(f => f.id === filterId)
      if (filter) {
        setFilterValues(filter.filters)
        setDefaultFilterApplied(true) // Prevent default filter from reapplying
      }
    } catch (error) {
      console.error('Error loading filter:', error)
      throw error
    }
  }

  const deleteFilter = async (filterId: string) => {
    try {
      const updatedFilters = savedFilters.filter(f => f.id !== filterId)
      setSavedFilters(updatedFilters)

      // Atualizar localStorage
      const storageKey = `filters_${entityType}_${userId || 'global'}`
      localStorage.setItem(storageKey, JSON.stringify(updatedFilters))
    } catch (error) {
      console.error('Error deleting filter:', error)
      throw error
    }
  }

  return {
    filterValues,
    setFilterValues: handleFiltersChange,
    savedFilters,
    loading,
    saveFilter,
    loadFilter,
    deleteFilter
  }
}
